import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { useHistory, useLocation } from 'react-router-dom';
import { SchedulingView, SchedulerDraggableId, SchedulerDroppableId, TimeBlock, ResourceGroupType, SchedulingBoardType, Period, SchedulerMode } from './SchedulingTypes';
import { generateDisplayDays, generateTimeSlots, generateHours, formatDateAsDayMonthYear } from './functions/schedulingDateUtils';
import { getCurrentDisplayDateAndPeriod, getDatePeriodFromUrl, setSchedulingUrl } from './functions/schedulingParamUtils';
import { getSchedulingSettings } from './functions/getSchedulingSettings';
import { generateResources } from './functions/generateResources';
import { DragDropEvent, DragDropProvider } from 'services/dragdrop/DragDropContext';
import SchedulingNavigation from './SchedulingNavigation';
import EmployeeScheduler from './EmployeeScheduler';
import ProjectScheduler from './project-scheduler/ProjectScheduler';
import PersonalCalendar from './personal-calendar/PersonalCalendar';
import { UserType } from '../../views/users/UserTypes';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import '../../style/scss/scheduling.scss'

const Scheduler: React.FC<{ renderPersonalCalendarOnly?: boolean }> = ({ renderPersonalCalendarOnly = false }) => {
    const history = useHistory();
    const location = useLocation();
    const { modalLoading } = useModal();
    const { userHash, activeFeatures } = useAuthContext();
    const { setShowFooterLogo } = useGlobalContext();
    const { environmentSettings, userPreferences } = useSettings();
    // Drop the period from the initial url so a fresh page load defaults to the saved preference
    const [currentParams, setCurrentParams] = useState<{ period?: string; date?: string }>(() => ({ ...getDatePeriodFromUrl(location), period: undefined }));
    const [schedulingBoard, setSchedulingBoard] = useState<SchedulingBoardType | null>(null);
    const [view, setView] = useState<SchedulingView | null>(null);
    const [days, setDays] = useState<Date[]>([]);
    const [hours, setHours] = useState<string[] | undefined>(undefined)
    const [timeSlots, setTimeSlots] = useState<string[] | undefined>(undefined);
    const [resourceGroups, setResourceGroups] = useState<ResourceGroupType[]>([]);
    const [selectableResourceGroups, setSelectableResourceGroups] = useState<ResourceGroupType[]>([]);
    const [selectedResourceGroups, setSelectedResourceGroups] = useState<number[]>([]);
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
    const [dragEvent, setDragEvent] = useState<DragDropEvent<SchedulerDraggableId, SchedulerDroppableId> | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [loading, setLoading] = useState<string>('');
    const preventNextUrlChange = useRef(false);

    // Disable footer logo on mount and restore it when leaving the page
    useEffect(() => {
        setShowFooterLogo(false);
        return () => setShowFooterLogo(true);
    }, []);

    // Remove the period from the url on mount so a refresh defaults to the saved preference
    useEffect(() => {
        const urlParams = getDatePeriodFromUrl(location);
        if (urlParams.period) {
            preventNextUrlChange.current = true;
            history.replace({
                pathname: location.pathname,
                search: urlParams.date ? `?date=${urlParams.date}` : '',
            });
        }
    }, []);

    // Fetch the scheduling boards and its resources
    const { response: fetchedSchedulingBoards, loading: schedulingBoardsLoading } = useFetchData({ 
        apiUrl: 'get_scheduling_board_resources' 
    });

    // Fetch the time blocks
    const { response: fetchedTimeBlocks } = useFetchData({ 
        apiUrl: 'get_timeblock_list', 
        params: useMemo(() => ({ deleted: false }), []) 
    });

    // Fetch the users
    const { response: fetchedUsers } = useFetchData({ apiUrl: 'get_user_list' });

    // Show loader if a modal is opening
    useEffect(() => {
        if (modalLoading) {
            setLoading('show-loader');
        } else {
            setLoading('success');
        }
    }, [modalLoading]);

    // Set the fetched scheduling boards
    useEffect(() => {
        if (fetchedSchedulingBoards) {
            // Get the first ordered scheduling board as default
            const defaultSchedulingBoard = (fetchedSchedulingBoards.boards[0]);

            // Set the default scheduling board
            setSchedulingBoard(defaultSchedulingBoard);

            // Set the user division resource group if given
            if (fetchedSchedulingBoards.user_division_resource_group) {
                setSelectedResourceGroups([fetchedSchedulingBoards.user_division_resource_group])
            }
        }
    }, [fetchedSchedulingBoards]);

    // Set the fetched resources
    useEffect(() => {
        if (fetchedSchedulingBoards && environmentSettings) {
            const { selectableResourceGroups, currentResourceGroups } = generateResources(
                fetchedSchedulingBoards.boards[0], days, selectedResourceGroups, environmentSettings
            )
            setResourceGroups(currentResourceGroups);
            setSelectableResourceGroups(selectableResourceGroups);
        }
    }, [fetchedSchedulingBoards, selectedResourceGroups, days, environmentSettings]);

    // Set the fetched timeblocks
    useEffect(() => {
        if (fetchedTimeBlocks) setTimeBlocks(fetchedTimeBlocks);
    }, [fetchedTimeBlocks]);

    // Set the fetched users
    const users = useMemo(() => {
        if (!fetchedUsers?.results) return [];

        return fetchedUsers.results
            .filter((user: UserType): user is typeof user & { full_name: string } => user.is_active && typeof user.full_name === 'string')
            .sort((a: UserType, b: UserType) => a.full_name!.localeCompare(b.full_name!));
    }, [fetchedUsers]);

    // Set the default selected user
    useEffect(() => {
        if (users.length && selectedUser === null) {
            // Get the active field users
            const activeFieldUsers = users.filter((user: UserType) => user.is_active && user.field_access);

            // Select the current logged in user, or fallback to the first user
            if (activeFieldUsers.length) {
                const currentUser = activeFieldUsers.find((user: UserType) => user.user_hash === userHash);
                if (currentUser) {
                    setSelectedUser(currentUser.id);
                } else {
                    setSelectedUser(activeFieldUsers[0].id);
                }
            }
        }

    }, [users, selectedUser, userHash]);

    // Apply scheduling settings to the view
    useEffect(() => {
        if (environmentSettings) {
            // Get the current display date and period with the preference as fallback
            const { displayDate, period } = getCurrentDisplayDateAndPeriod(currentParams, userPreferences?.scheduling_default_view_period);

            // Get the scheduling settings from the environment and user preferences
            const { updatedView } = getSchedulingSettings(
                environmentSettings, userPreferences, displayDate, period, view, renderPersonalCalendarOnly, activeFeatures
            );

            // Update the view and show inbox state
            if (updatedView) setView(updatedView);
        }
    }, [environmentSettings, currentParams, userPreferences, activeFeatures]);

    // Apply or update days, hours, timeslot and params after view change
    useEffect(() => {
        if (view) {
            // Update days, hours and timeslots
            setDays(generateDisplayDays(view));
            setHours(generateHours(view));
            setTimeSlots(generateTimeSlots(view));

            // Generate the new url params
            const newUrlParams = {
                period: view.period,
                date: formatDateAsDayMonthYear(view.displayDate),
            };

            // Get the date and period from the url
            const currentUrlParams = getDatePeriodFromUrl(location);

            // Update the scheduling url if the date and period have changed
            if (newUrlParams.period !== currentUrlParams.period || newUrlParams.date !== currentUrlParams.date) {
                preventNextUrlChange.current = true;
                setSchedulingUrl(newUrlParams, history, location);
            };
        }
    }, [view]);

    // Update the view if the current params are changed
    useEffect(() => {
        // Get the current display date and period with the preference as fallback
        const { displayDate, period } = getCurrentDisplayDateAndPeriod(currentParams, userPreferences?.scheduling_default_view_period);

        // Update the view
        setView(prev => {
            if (!prev) return null;

            // Keep the current period when the url does not hold one
            const nextPeriod = period ?? prev.period;

            // Determine if the values are equal, return the prev view state
            const areEqual = (prev.displayDate.getTime() === displayDate.getTime() && prev.period === nextPeriod);
            if (areEqual) return prev;

            // Otherwise add the new date and period to the view
            return { ...prev, displayDate, period: nextPeriod };
        });
    }, [currentParams]);
    
    // Update the params state when the url params have changed
    useEffect(() => {
        // Prevent next url change
        if (preventNextUrlChange.current) {
            preventNextUrlChange.current = false;
            return;
        }

        // Get the date and period from the url
        const newUrlParams = getDatePeriodFromUrl(location);

        // Prevent unnecessary loops
        if (JSON.stringify(newUrlParams) !== JSON.stringify(currentParams)) {
            setCurrentParams(newUrlParams);
        }
    }, [location.search]);

    // Determine if the scheduling board is ready to render
    const isReadyToRender = schedulingBoard !== null && view !== null;

    if (!view) return null;

    return (
        <>
            {/* Render scheduling navigation */}
            <SchedulingNavigation
                days={days}
                view={view}
                loading={loading}
                selectableResourceGroups={selectableResourceGroups}
                selectedResourceGroups={selectedResourceGroups}
                selectedUser={selectedUser}
                users={users}
                onViewChange={(value) => setView(value)}
                onSetDays={(value) => setDays(value)}
                onSetSelectedResourceGroups={(value) => setSelectedResourceGroups(value)}
                onSetSelectedUser={setSelectedUser}
                onRefresh={() => setRefreshTrigger(prev => prev + 1)}
            />

            {/* Initialize the scheduler drag drop provider */}
            {isReadyToRender && (
                <DragDropProvider<SchedulerDraggableId, SchedulerDroppableId>
                    onDragEnd={(event) => setDragEvent(event)}>

                    {/* Render the team scheduler */}
                    {view.mode === 'team' && (
                        view.viewType === 'personal_calendar' ? (
                            selectedUser && (
                                <PersonalCalendar
                                    view={view}
                                    selectedUser={selectedUser}
                                    selectedUserHash={users.find((user: UserType) => user.id === selectedUser)?.user_hash ?? null}
                                    days={days}
                                    timeSlots={timeSlots}
                                    dragResult={dragEvent}
                                    refreshTrigger={refreshTrigger}
                                    onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                                />
                            )
                        ) : (
                            <EmployeeScheduler
                                view={view}
                                schedulingBoard={schedulingBoard}
                                resourceGroups={resourceGroups}
                                selectedResourceGroups={selectedResourceGroups}
                                days={days}
                                hours={hours}
                                timeSlots={timeSlots}
                                timeBlocks={timeBlocks}
                                dragResult={dragEvent}
                                refreshTrigger={refreshTrigger}
                                setResourceGroups={setResourceGroups}
                                setView={setView}
                                setLoading={setLoading}
                                onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                            />
                        )
                    )}

                    {/* Render the project scheduler */}
                    {view.mode === 'projects' && (
                        <ProjectScheduler
                            view={view}
                            days={days}
                            hours={hours}
                            timeSlots={timeSlots}
                            dragResult={dragEvent}
                            refreshTrigger={refreshTrigger}
                            setLoading={setLoading}
                        />
                    )}
                </DragDropProvider>
            )}
        </>
    )
}

export default Scheduler;